import { useState } from 'react';
import axios from 'axios';
import { Loader2, Lock, Sparkles, AlertTriangle, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 84k Phase 5.3 — Total Return Race fair-match rule: every
// entry must contain exactly 5 tickers so % return is compared on
// equal footing. Watchlists with fewer than 5 tickers are blocked
// at both the modal step and the backend `/enter` endpoint.
const REQUIRED_TICKERS = 5;

function ConfirmModal({ watchlist, week, targetWeek, onCancel, onConfirm, busy }) {
  // Round 84k Phase 4.2 — distinguish "this week" vs "next week"
  // entries so users know what they're signing up for.
  const isNextWeek = !!targetWeek?.is_next_week;
  const tWeek = targetWeek || week;
  const tickerCount = (watchlist.tickers || []).length;
  const isIncomplete = tickerCount < REQUIRED_TICKERS;
  const needed = REQUIRED_TICKERS - tickerCount;
  let mondayLabel = '';
  let fridayLabel = '';
  try {
    const open = new Date(tWeek.lock_open_at);
    const close = new Date(tWeek.lock_close_at);
    const fmt = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    mondayLabel = fmt(open);
    fridayLabel = fmt(close);
  } catch { /* noop */ }
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="leaderboard-confirm-modal">
      <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isIncomplete
                ? 'bg-rose-100 dark:bg-rose-950/50'
                : isNextWeek
                ? 'bg-sky-100 dark:bg-sky-950/50'
                : 'bg-amber-100 dark:bg-amber-950/50'
            }`}>
              {isIncomplete ? (
                <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              ) : (
                <Lock className={`w-5 h-5 ${
                  isNextWeek
                    ? 'text-sky-600 dark:text-sky-400'
                    : 'text-amber-600 dark:text-amber-400'
                }`} />
              )}
            </div>
            <div>
              <h3 className="text-base font-bold">
                {isIncomplete
                  ? 'Watchlist is incomplete'
                  : isNextWeek ? 'Queue for next week?' : 'Lock in your pick?'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {isIncomplete
                  ? `Need ${needed} more ticker${needed === 1 ? '' : 's'} to enter.`
                  : isNextWeek ? `Race opens Mon ${mondayLabel}.` : 'This cannot be changed once confirmed.'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-muted" aria-label="Close" data-testid="leaderboard-confirm-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="rounded-lg bg-muted/30 border border-border p-3 mb-3">
          <p className="text-xs text-muted-foreground mb-1">Your entry</p>
          <p className="text-sm font-bold mb-1">
            {watchlist.name}{' '}
            <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ml-1 ${
              isIncomplete
                ? 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
                : 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
            }`}>
              {tickerCount}/{REQUIRED_TICKERS}
            </span>
          </p>
          <div className="flex flex-wrap gap-1">
            {(watchlist.tickers || []).map((t) => (
              <span key={t} className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300">
                {t}
              </span>
            ))}
            {isIncomplete
              ? Array.from({ length: needed }).map((_, i) => (
                  <span key={`empty-${i}`} className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border border-dashed border-rose-300 dark:border-rose-700 text-rose-500 dark:text-rose-400">
                    + add
                  </span>
                ))
              : null}
          </div>
        </div>
        {isIncomplete ? (
          <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 p-3 mb-4" data-testid="leaderboard-confirm-incomplete-notice">
            <p className="text-xs font-bold text-rose-800 dark:text-rose-300 mb-1 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>The Total Return Race requires a full {REQUIRED_TICKERS}-ticker watchlist.</span>
            </p>
            <p className="text-xs text-rose-800 dark:text-rose-300 leading-snug pl-6">
              This keeps every entry on the same footing — a 3-stock list would have an unfair advantage (or disadvantage) over a 5-stock list when we average % return.
            </p>
            <div className="mt-3 pl-6">
              <p className="text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300 mb-1">How to fix it</p>
              <ol className="text-xs text-rose-800 dark:text-rose-300 leading-relaxed list-decimal list-inside space-y-0.5">
                <li>Open the <strong>Watchlists</strong> tab at the top of the Community page.</li>
                <li>Find <strong>{watchlist.name}</strong> and tap <strong>Edit</strong>.</li>
                <li>Add {needed} more ticker{needed === 1 ? '' : 's'} (search auto-suggests).</li>
                <li>Save, then come back to the Leaderboard tab and enter again.</li>
              </ol>
            </div>
          </div>
        ) : isNextWeek ? (
          <div className="rounded-lg bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800 p-3 mb-4" data-testid="leaderboard-confirm-next-week-notice">
            <p className="text-xs text-sky-800 dark:text-sky-300 leading-snug flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                This week's race already locked. Your pick will be <strong>queued for next week's race</strong> ({mondayLabel} – {fridayLabel}). Starting prices snapshot at Monday 9 AM ET when the race begins. <strong>You can't switch picks for that week</strong> once confirmed.
              </span>
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 mb-4">
            <p className="text-xs text-amber-800 dark:text-amber-300 leading-snug flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                You're entering <strong>{week.theme_name}</strong>. Starting prices snapshot the moment you confirm, and the leaderboard freezes Friday 4:15 PM ET. <strong>You can't switch picks this week</strong> — even to your other watchlists.
              </span>
            </p>
          </div>
        )}
        <div className="flex gap-2">
          {isIncomplete ? (
            <Button onClick={onCancel} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="leaderboard-confirm-incomplete-ok">
              Got it, I'll add more tickers
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onCancel} disabled={busy} className="flex-1" data-testid="leaderboard-confirm-no">
                No, take me back
              </Button>
              <Button onClick={onConfirm} disabled={busy} className={`flex-1 ${isNextWeek ? 'bg-sky-600 hover:bg-sky-700' : 'bg-violet-600 hover:bg-violet-700'}`} data-testid="leaderboard-confirm-yes">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, lock it in'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LeaderboardPickScreen({ week, targetWeek, watchlists, onPicked }) {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const isNextWeek = !!targetWeek?.is_next_week;
  const tWeek = targetWeek || week;
  let nextOpenLabel = '';
  try {
    nextOpenLabel = new Date(tWeek.lock_open_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { /* noop */ }

  const handleConfirm = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/community/leaderboard/enter`,
        { watchlist_id: selected.watchlist_id, confirm: true },
        { withCredentials: true },
      );
      toast.success('Pick locked. Good luck!');
      onPicked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Entry failed.');
      setBusy(false);
    }
  };

  if (!watchlists || watchlists.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-violet-300 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-950/20 p-6 text-center" data-testid="leaderboard-no-watchlists">
        <Sparkles className="w-8 h-8 text-violet-500 mx-auto mb-2" />
        <h3 className="text-base font-bold mb-1">You need a community watchlist to enter</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Head to the <strong>Watchlists</strong> tab and publish at least one 5-stock list, then come back here Monday.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="leaderboard-pick-screen">
      {isNextWeek && (
        <div className="rounded-xl border-2 border-sky-300 dark:border-sky-700 bg-sky-50/60 dark:bg-sky-950/30 p-4" data-testid="leaderboard-next-week-banner">
          <p className="text-xs font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300 mb-1 flex items-center gap-1">
            <Lock className="w-3 h-3" /> This week is locked
          </p>
          <p className="text-sm text-sky-900 dark:text-sky-200">
            Picks are now open for next week's race ({nextOpenLabel}). Confirm one of your watchlists and it will be queued to start Monday 9 AM ET.
          </p>
        </div>
      )}
      <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white p-5">
        <div className="flex items-center gap-2 text-xs uppercase font-bold tracking-wider opacity-90 mb-1">
          <Sparkles className="w-4 h-4" /> {isNextWeek ? 'Next week\'s challenge' : 'This week\'s challenge'}
        </div>
        <h2 className="text-2xl font-bold mb-1">{tWeek.theme_name}</h2>
        <p className="text-sm opacity-95">{tWeek.theme_blurb}</p>
        <p className="text-xs mt-2 opacity-80">
          {isNextWeek ? `Race opens ${nextOpenLabel} · freezes Friday 4:15 PM ET.` : 'Pick one watchlist. Locked until Friday 4:15 PM ET.'}
        </p>
      </div>
      <p className="text-xs text-muted-foreground px-1">
        Tap a watchlist to enter it — you'll confirm in a popup before it locks.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {watchlists.map((wl) => {
          const tCount = (wl.tickers || []).length;
          const incomplete = tCount < REQUIRED_TICKERS;
          return (
            <button
              type="button"
              key={wl.watchlist_id}
              onClick={() => setSelected(wl)}
              className={`text-left rounded-xl border bg-card hover:shadow-md transition p-4 group ${
                incomplete
                  ? 'border-rose-300 dark:border-rose-800 hover:border-rose-400 dark:hover:border-rose-600'
                  : 'border-border hover:border-violet-400 dark:hover:border-violet-600'
              }`}
              data-testid={`leaderboard-pick-${wl.watchlist_id}`}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <h3 className={`text-sm font-bold transition ${
                  incomplete
                    ? 'group-hover:text-rose-600 dark:group-hover:text-rose-400'
                    : 'group-hover:text-violet-600 dark:group-hover:text-violet-400'
                }`}>
                  {wl.name}
                </h3>
                <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded shrink-0 ${
                  incomplete
                    ? 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
                    : 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                }`}>
                  {tCount}/{REQUIRED_TICKERS}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(wl.tickers || []).map((t) => (
                  <span key={t} className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300">
                    {t}
                  </span>
                ))}
                {(!wl.tickers || wl.tickers.length === 0) ? (
                  <span className="text-[10px] text-muted-foreground italic">Empty list</span>
                ) : null}
              </div>
              <p className={`text-[11px] font-semibold ${
                incomplete
                  ? 'text-rose-600 dark:text-rose-400'
                  : 'text-violet-600 dark:text-violet-400'
              }`}>
                {incomplete
                  ? `Needs ${REQUIRED_TICKERS - tCount} more ticker${REQUIRED_TICKERS - tCount === 1 ? '' : 's'} to enter`
                  : 'Tap to enter →'}
              </p>
            </button>
          );
        })}
      </div>
      {selected ? (
        <ConfirmModal
          watchlist={selected}
          week={week}
          targetWeek={targetWeek}
          onCancel={() => { if (!busy) setSelected(null); }}
          onConfirm={handleConfirm}
          busy={busy}
        />
      ) : null}
    </div>
  );
}
